import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  useTheme,
  TableSortLabel,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  FormControl,
  Select,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { Item, ItemListResponse } from '../types/item.types';
import { itemService } from '../services/item.service';
import ItemImportDialog from './ItemImportDialog';

const ItemList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  
  // State for items data and pagination
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean | undefined>(undefined);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // State for item actions menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const menuOpen = Boolean(anchorEl);

  // Import dialog state
  const [importOpen, setImportOpen] = useState(false);

  // Fetch items data
  const fetchItems = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        isActive: isActive,
        sortBy,
        sortOrder,
      };
      
      const response = await itemService.getItems(params);
      setItems(response.data);
      setTotal(response.total);
    } catch (error: any) {
      console.error('Error fetching items:', error);
      enqueueSnackbar(
        error.message || 'Failed to load items',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchItems();
  }, [page, rowsPerPage, searchTerm, isActive, sortBy, sortOrder]);

  // Handle pagination
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Handle sorting
  const handleSort = (property: string) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  // Handle item actions menu
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: Item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  // Handle item status toggle
  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await itemService.toggleItemStatus(id, !currentStatus);
      enqueueSnackbar('Item status updated successfully', { variant: 'success' });
      fetchItems();
    } catch (error: any) {
      console.error('Error toggling item status:', error);
      enqueueSnackbar(
        error.message || 'Failed to update item status',
        { variant: 'error' }
      );
    }
  };

  // Handle item delete
  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await itemService.deleteItem(id);
        enqueueSnackbar('Item deleted successfully', { variant: 'success' });
        fetchItems();
      } catch (error: any) {
        console.error('Error deleting item:', error);
        enqueueSnackbar(
          error.message || 'Failed to delete item',
          { variant: 'error' }
        );
      }
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchItems();
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Toolbar
            sx={{
              pl: { sm: 2 },
              pr: { xs: 1, sm: 1 },
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              Items
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setImportOpen(true)}
              >
                Import Items
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/settings/items/new')}
              >
                Add Item
              </Button>
              <Tooltip title="Refresh">
                <IconButton onClick={handleRefresh} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
          <Toolbar
            sx={{
              pl: { sm: 2 },
              pr: { xs: 1, sm: 1 },
              display: 'flex',
              justifyContent: 'flex-start',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <TextField
              size="small"
              placeholder="Search items..."
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{ minWidth: 250, flex: '1 1 auto' }}
              InputProps={{
                startAdornment: <FilterListIcon color="action" sx={{ mr: 1 }} />,
              }}
            />
            <FormControl size="small" variant="outlined" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={isActive === undefined ? 'all' : isActive ? 'active' : 'inactive'}
                onChange={(e) => {
                  const value = e.target.value;
                  setIsActive(
                    value === 'all' ? undefined : 
                    value === 'active' ? true : false
                  );
                  setPage(0);
                }}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Toolbar>
          <ItemImportDialog
            open={importOpen}
            onClose={() => setImportOpen(false)}
            onImported={() => {
              setImportOpen(false);
              fetchItems();
            }}
          />
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'itemCode'}
                      direction={sortBy === 'itemCode' ? sortOrder : 'asc'}
                      onClick={() => handleSort('itemCode')}
                    >
                      Code
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'itemName'}
                      direction={sortBy === 'itemName' ? sortOrder : 'asc'}
                      onClick={() => handleSort('itemName')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>Sub Group</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell align="right">Cost Price</TableCell>
                  <TableCell align="right">Selling Price</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ mt: 1 }}>Loading items...</Typography>
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="textSecondary">
                        No items found. {searchTerm ? 'Try a different search term.' : 'Click "Add Item" to get started.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.itemCode}</TableCell>
                      <TableCell>{item.itemName}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.itemType}
                          size="small"
                          color={item.itemType === 'INVENTORY' ? 'primary' : 'secondary'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{item.itemGroup}</TableCell>
                      <TableCell>
                        {item.itemSubGroup ? item.itemSubGroup.replace('_', '/') : '-'}
                      </TableCell>
                      <TableCell>{item.unitOfMeasure}</TableCell>
                      <TableCell align="right">
                        {item.costPrice?.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell align="right">
                        {item.sellingPrice?.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.isActive ? 'Active' : 'Inactive'}
                          color={item.isActive ? 'success' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/settings/items/${item.id}`)}
                              color="primary"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/settings/items/${item.id}/edit`)}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="More">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, item)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: 2 }}
          />
        </CardContent>
      </Card>
      
      {/* Item Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem 
          onClick={() => {
            if (selectedItem) {
              handleToggleStatus(selectedItem.id, selectedItem.isActive);
            }
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            {selectedItem?.isActive ? (
              <DeleteIcon fontSize="small" color="error" />
            ) : (
              <RefreshIcon fontSize="small" color="primary" />
            )}
          </ListItemIcon>
          <ListItemText>
            {selectedItem?.isActive ? 'Deactivate' : 'Activate'}
          </ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (selectedItem) {
              handleDelete(selectedItem.id);
            }
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ItemList;
